import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Film, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const IN_PROGRESS = ["RENDERING", "QUEUED", "PENDING"];

function InProgressRow({ m }) {
    const [bp, setBp] = useState(null);
    useEffect(() => {
        const load = () => api.get(`/modules/${m.id}/build-progress`).then((r) => setBp(r.data)).catch(() => {});
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [m.id]);
    return (
        <Link to={`/videos/${m.id}?step=video`} data-testid={`home-in-progress-${m.id}`} className="block rounded-lg border border-neutral-200 bg-white p-4 hover:border-neutral-400">
            <div className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-neutral-900">{m.name}</span>
                <span className="text-xs text-neutral-500">{bp ? `${Math.round(bp.percent || 0)}% · ${bp.eta_display || ""}` : "…"}</span>
            </div>
            <Progress value={bp?.percent || 0} className="mt-3 h-1.5" />
            {bp?.current_step_message && <div className="mt-2 text-xs text-neutral-500">{bp.current_step_message}</div>}
        </Link>
    );
}

export default function Home() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [mods, setMods] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/modules").then((r) => setMods(r.data)).catch((e) => setErr(formatApiError(e)));
    }, []);
    const list = mods || [];
    const inProgress = list.filter((m) => IN_PROGRESS.includes(m.render_status));
    const finished = list.filter((m) => m.render_status === "READY")
        .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")).slice(0, 6);

    return (
        <div data-testid="home-page" className="space-y-10">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-medium tracking-tight text-neutral-900">Welcome back, {user?.name?.split(" ")[0]}.</h1>
                    <p className="mt-2 text-sm text-neutral-500">Turn a document into a training video.</p>
                </div>
                <Button data-testid="home-new-video-btn" onClick={() => nav("/onboard")} className="h-14 rounded-full bg-neutral-900 px-8 text-base text-white hover:bg-neutral-800">
                    <Plus size={18} className="mr-2" /> New training video
                </Button>
            </div>
            {err && <div data-testid="home-error" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{err}</div>}
            <section className="space-y-3">
                <h2 className="text-base font-medium text-neutral-900">In progress</h2>
                {inProgress.length ? inProgress.map((m) => <InProgressRow key={m.id} m={m} />) : (
                    <div data-testid="home-no-in-progress" className="text-sm text-neutral-500">Nothing is building right now.</div>
                )}
            </section>
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-base font-medium text-neutral-900">Recently finished</h2>
                    <Link to="/videos" data-testid="home-all-videos" className="text-xs text-neutral-500 underline">All videos</Link>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {finished.map((m) => (
                        <Link key={m.id} to={`/videos/${m.id}?step=video`} data-testid={`home-finished-${m.id}`} className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white p-4 hover:border-neutral-400">
                            <Film size={18} className="mt-0.5 text-neutral-500" />
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-neutral-900">{m.name}</div>
                                <div className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
                                    {m.status === "PUBLISHED" && <CheckCircle2 size={12} className="text-emerald-600" />}
                                    {m.status === "PUBLISHED" ? "Published" : "Ready to publish"}
                                    {m.actual_duration_seconds ? ` · ${(m.actual_duration_seconds / 60).toFixed(1)} min` : ""}
                                </div>
                            </div>
                        </Link>
                    ))}
                    {mods && !finished.length && <div data-testid="home-no-finished" className="text-sm text-neutral-500">No finished videos yet.</div>}
                </div>
            </section>
        </div>
    );
}
